import { useState, useEffect } from 'react';
import timer from '@/assets/daily-promotions/promotion-timer.svg';
import type { FirebaseTimestamp } from '@skillscratch/shared-types';
import { Timestamp } from 'firebase/firestore';

export type SerializedFirebaseTimestamp = {
  _seconds: number;
  _nanoseconds: number;
};

function convertToTimestamp(data: SerializedFirebaseTimestamp | undefined): Timestamp | null {
  if (!data) return null;

  // console.log('data: ');
  // console.log(data);
  
  if (data) {
    // console.log('did we make it here');
    // console.log(new Timestamp(data._seconds, data._nanoseconds));
    return new Timestamp(data._seconds, data._nanoseconds);
  }

  return null;
};

interface PromoTimerProps {
  promotionEndTimestamp: SerializedFirebaseTimestamp | undefined;
  isPromotionEndTimestampLoading: boolean;
  /**
   * Optional custom background color for the timer
   */
  backgroundColor?: string;
  /**
   * Whether to show the clock icon
   */
  showIcon?: boolean;
  /**
   * Whether to use inline styling (no background)
   */
  inline?: boolean;
}

export type GetPromotionEndTimestampResult = {
  promotionEndTimestamp: FirebaseTimestamp;
};

export function PromoTimer({ 
  promotionEndTimestamp,
  isPromotionEndTimestampLoading,
  backgroundColor = "#cc2427", 
  showIcon = true,
  inline = false 
}: PromoTimerProps) {
  // State to hold the time remaining
  const [timeRemaining, setTimeRemaining] = useState('24:00:00');
  
  useEffect(() => {
    // Don't do anything until we have the timestamp
    if (isPromotionEndTimestampLoading || !promotionEndTimestamp) {
      return;
    }

    console.log('[promotion]', promotionEndTimestamp);
    
    const calculateTimeRemaining = () => {
      const now = new Date();
      
      // Convert Firebase timestamp to JS Date
      // Assuming promotionEndTimestamp has seconds and nanoseconds properties
      const firebaseTimestamp = convertToTimestamp(promotionEndTimestamp) as FirebaseTimestamp;

      const endDate = new Date(firebaseTimestamp.seconds * 1000);
      
      // Calculate time difference
      const diff = endDate.getTime() - now.getTime();
      
      // If time has passed, return zeros
      if (diff <= 0) {
        return '00:00:00';
      }
      
      // Format time remaining
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };
    
    // Set initial time
    setTimeRemaining(calculateTimeRemaining());
    
    // Update time every second
    const intervalId = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining());
    }, 1000);
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [promotionEndTimestamp, isPromotionEndTimestampLoading]);
  
  if (inline) {
    return (
      <span className="inline-flex items-center text-s font-medium ml-1 bg-red-500 p-[3px] px-1.5 rounded-sm text-white">
        {showIcon && <img src={timer} className="pr-1 h-4" alt="Clock icon" />}
        <span className="font-semibold tabular-nums">{timeRemaining}</span>
      </span>
    );
  }
  
  return (
    <div 
      className="flex items-center text-sm font-medium px-2 py-1 rounded-sm" 
      style={{ backgroundColor, color: 'white' }}
    >
      {showIcon && <img src={timer} className="pr-1" alt="Clock icon" width="22" height="18" />}
      <span className="font-semibold min-w-[90px] text-center tabular-nums">{timeRemaining}</span>
    </div>
  );
}